/**
 * usePartnerScoped — is the signed-in user partner-scoped (RBAC-3.90)? RBAC-3.92, RBAC-3.93.
 *
 * Wires the database's `partner_scoped()` to the pure decisions in `@/lib/partnerScope`. It decides
 * what to OFFER and nothing else: RLS and the backend refuse a partner's writes whatever this says.
 *
 * ⚠️ `loading` is true until the answer arrives, and `canWriteProject` offers nothing until it is
 * false, so a partner never sees a button that then disappears. The cost is that everyone else sees
 * write controls a beat late, once per session: the answer is cached per user (RBAC-3.93), because
 * every section of the project screen asks.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { canWriteProject, readPartnerScoped, type PartnerScopeState } from '@/lib/partnerScope';

export const partnerScopedKeys = {
  byUser: (userId: string | null) => ['partnerScoped', userId] as const,
};

export function usePartnerScoped(): PartnerScopeState {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const { data } = useQuery({
    queryKey: partnerScopedKeys.byUser(userId),
    queryFn: () => readPartnerScoped(() => supabase.rpc('partner_scoped')),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  return { loading: data === undefined, scoped: data === true };
}

/** RBAC-3.93: may the caller be offered a project write? False until known, false for a partner. */
export function useCanWriteProject(): boolean {
  return canWriteProject(usePartnerScoped());
}
